'use client';

import { useId, useState } from 'react';
import { CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { Button } from '@/components/storefront/ui/button';
import { Input } from '@/components/storefront/ui/input';
import { subscribeToNewsletter } from '@/lib/newsletter/actions';
import { trackLead } from '@/lib/tracking/events';
import type { NewsletterConfig } from '@/lib/structured-builder/widget-types/newsletter';
type Props = {
  config: NewsletterConfig;
  locale: string;
};
type Outcome = 'idle' | 'subscribed' | 'already_subscribed' | 'invalid_email' | 'error';
const MESSAGES = {
  already_subscribed: {
    ar: 'هذا البريد مشترك بالفعل.',
    en: 'This email is already subscribed.'
  },
  invalid_email: {
    ar: 'يرجى إدخال بريد إلكتروني صحيح.',
    en: 'Please enter a valid email address.'
  },
  error: {
    ar: 'تعذّر الاشتراك الآن، يرجى المحاولة لاحقاً.',
    en: "Couldn't subscribe right now. Please try again later."
  }
} as const;
export function NewsletterClient({
  config,
  locale
}: Props) {
  const isAr = locale === 'ar';
  const inputId = useId();
  const errorId = useId();
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>('idle');
  const heading = isAr ? config.heading_ar : config.heading_en;
  const subtitle = isAr ? config.subtitle_ar : config.subtitle_en;
  const placeholder = isAr ? config.placeholder_ar : config.placeholder_en;
  const btnLabel = isAr ? config.button_label_ar : config.button_label_en;
  const successMsg = isAr ? config.success_message_ar : config.success_message_en;
  const alignCls = config.align === 'center' ? 'text-center items-center' : config.align === 'end' ? 'text-end items-end' : 'text-start items-start';

  // Field and button share one row from `sm` up, except in the explicitly
  // stacked style. On phones they always stack at full width.
  const isRow = config.style !== 'stacked';
  const isCard = config.style === 'card';
  const done = outcome === 'subscribed' || outcome === 'already_subscribed';
  const errorMsg = outcome === 'invalid_email' || outcome === 'error' ? MESSAGES[outcome][isAr ? 'ar' : 'en'] : null;
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (pending) return;
    if (!email.trim()) {
      setOutcome('invalid_email');
      return;
    }
    setPending(true);
    try {
      const result = await subscribeToNewsletter(email, 'newsletter_widget');
      setOutcome(result.status);
      if (result.status === 'subscribed') trackLead('newsletter');
    } catch {
      setOutcome('error');
    } finally {
      setPending(false);
    }
  }
  return <div className={`flex flex-col ${alignCls} ${isCard ? 'bg-subtle rounded-2xl px-6 py-10 md:p-12' : ''}`}>
      {heading && <h2 className="text-2xl md:text-3xl font-bold text-[var(--widget-fg,var(--foreground))] text-balance">{heading}</h2>}
      {subtitle && <p className="text-base text-[var(--widget-fg,var(--muted-foreground))] mt-2 max-w-lg text-pretty">{subtitle}</p>}

      <div className="mt-6 w-full max-w-md">
        {done ? <output className="inline-flex items-center gap-2 min-h-[var(--field-height)] px-4 rounded-[var(--radius-field)] bg-background border border-border text-base font-medium text-foreground">
            <CheckCircle size={20} weight="fill" className="shrink-0 text-primary" aria-hidden />
            {outcome === 'subscribed' ? successMsg : MESSAGES.already_subscribed[isAr ? 'ar' : 'en']}
          </output> : <form onSubmit={handleSubmit} noValidate className={`flex flex-col gap-3 ${isRow ? 'sm:flex-row sm:gap-2' : ''}`}>
            <label htmlFor={inputId} className="sr-only">{placeholder}</label>
            {/* Error sits under the field it describes, not under the button. */}
            <div className={`w-full min-w-0 ${isRow ? 'sm:flex-1' : ''}`}>
              <Input id={inputId} name="email" type="email" autoComplete="email" inputMode="email" dir="ltr" value={email} onChange={e_0 => {
            setEmail(e_0.target.value);
            if (errorMsg) setOutcome('idle');
          }} placeholder={placeholder} invalid={!!errorMsg} aria-describedby={errorMsg ? errorId : undefined} className="rtl:placeholder:text-right" />
              {errorMsg && <p id={errorId} role="alert" className="mt-2 inline-flex items-center gap-1.5 text-sm text-danger">
                  <WarningCircle size={16} weight="fill" className="shrink-0" aria-hidden />
                  {errorMsg}
                </p>}
            </div>
            <Button variant="solid" color={config.button_color} size="md" type="submit" loading={pending} className={`w-full shrink-0 ${isRow ? 'sm:w-auto' : ''}`}>
              {btnLabel}
            </Button>
          </form>}
      </div>
    </div>;
}